import type { ReactNode } from 'react';
import { ACCENTS } from '@featit/shared/brand/colour-accents';
import { StudyCanvas } from '@/components/accents/StudyCanvas';
import { useAccents } from '@/components/accents/useAccents';
import { sessionWaitingStudy } from '@/components/waiting/waitingSession';

/** ACC-9: the study's box, the glyph's own place (a 40 px icon box). */
export const EMPTY_STATE_STUDY_PX = 40;

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  /**
   * I-1076: a `ReactNode`, not only a `string` — some empty states need a link inline (e.g. "N
   * decisions wait elsewhere. See decisions for me."), and a `string` is already a valid one so
   * every existing caller is unchanged.
   */
  description?: ReactNode;
  action?: ReactNode;
}

/**
 * F-0579 ACC-9 (`docs/concept-colour-accents.md`): in Colour an empty state shows a waiting study's
 * rest shape, still and in the palette, in place of its grey glyph: the session's own study, the one
 * its waits draw (`sessionWaitingStudy`). Quiet keeps the glyph.
 */
export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  const accents = useAccents();
  return (
    <div
      // I-1396 (CFC-f4e): its action is pressed with a finger at every width (`index.css`).
      data-coarse-state=""
      className="flex flex-col items-center justify-center px-6 py-20 text-center"
    >
      {icon && accents === ACCENTS.COLOUR ? (
        <div className="mb-4" data-empty-state-study="">
          <StudyCanvas study={sessionWaitingStudy().study.id} sizePx={EMPTY_STATE_STUDY_PX} />
        </div>
      ) : icon ? (
        <div className="mb-4 text-gray-300">{icon}</div>
      ) : null}
      <h3 className="text-[15px] font-medium text-gray-900 dark:text-gray-100">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-gray-500">{description}</p>
      )}
      {/* An action component that draws nothing (IU-17 PCH-1's way back) leaves no gap. */}
      {action && <div className="mt-5 empty:hidden">{action}</div>}
    </div>
  );
}
